import {
  LuLock,
  LuLockOpen,
  LuCalendar,
  LuClock,
  LuCircleCheck,
} from "react-icons/lu";
import { Revision } from "../Tabs/RevisionsTab";
import useLockSelection from "../Revisions/hooks/useLockSelection";
import LoaderButton from "@/components/shared/LoaderButton";

type LockSelectionProps = {
  mostRecentRevision: Revision | null;
  lockedRevision: Revision | null;
  lockedRevisionId: string | null;
  lockDisabled: boolean;
  lockDisabledReason?: string;
  unlockDisabled: boolean;
  unlockDisabledReason?: string;
  hasErrors: boolean;
  canCertify: boolean;
  isLocking?: boolean;
  isUnlocking?: boolean;
  lockError?: Error | null;
  unlockError?: Error | null;
  onLock: (revisionId: string) => void;
  onUnlock: (revisionId: string) => void;
  onReplaceLock: (newRevisionId: string) => void;
};

function formatPercent(count: number, total: number): string {
  if (total === 0) return "0%";
  return `${Math.round((count / total) * 100)}%`;
}

function ConfirmationDialog({
  open,
  title,
  message,
  confirmLabel,
  confirmVariant = "danger",
  confirmIcon,
  isLoading = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  confirmVariant?: "danger" | "primary" | "amber";
  confirmIcon?: React.ReactNode;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;

  const confirmBg =
    confirmVariant === "danger"
      ? "#dc2626"
      : confirmVariant === "amber"
        ? "#D97706"
        : "#059669";

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.6)", zIndex: 1050 }}
      onClick={isLoading ? undefined : onCancel}
    >
      <div
        className="rounded-4 shadow-lg"
        style={{
          backgroundColor: "#fff",
          width: "100%",
          maxWidth: "520px",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 pt-4 pb-4">
          <h5 className="fw-bold mb-3" style={{ fontSize: "1.25rem" }}>
            {title}
          </h5>
          <p
            className="mb-0"
            style={{ fontSize: "0.95rem", lineHeight: 1.6, color: "#374151" }}
          >
            {message}
          </p>
        </div>

        <div
          className="d-flex justify-content-end gap-3 px-4 py-3"
          style={{
            backgroundColor: "#f9fafb",
            borderTop: "1px solid #f1f5f9",
          }}
        >
          <button
            type="button"
            className="btn"
            onClick={onCancel}
            disabled={isLoading}
            style={{
              backgroundColor: "#ffffff",
              border: "1px solid #d1d5db",
              color: "#374151",
              fontSize: "0.9rem",
              fontWeight: 500,
              padding: "0.55rem 1.5rem",
              borderRadius: "0.5rem",
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <LoaderButton
            variant=""
            onClick={onConfirm}
            loading={isLoading}
            loadingText="Processing..."
            className="d-flex align-items-center justify-content-center gap-2"
            style={{
              backgroundColor: confirmBg,
              border: `1px solid ${confirmBg}`,
              color: "#ffffff",
              fontSize: "0.9rem",
              fontWeight: 600,
              padding: "0.55rem 1.5rem",
              borderRadius: "0.5rem",
              minWidth: "160px",
            }}
          >
            {confirmIcon}
            {confirmLabel}
          </LoaderButton>
        </div>
      </div>
    </div>
  );
}

function StatBox({
  label,
  value,
  variant = "default",
}: {
  label: string;
  value: string | number;
  variant?: "default" | "error" | "warning";
}) {
  const isZero = value === 0 || value === "0" || value === "0%";
  const useNeutral = isZero && (variant === "error" || variant === "warning");

  let bg = "#f8fafc";
  let border = "#ebebeb";
  let color = "#0f172a";
  let labelColor = "#475569";

  if (!useNeutral && variant === "error") {
    bg = "#fef2f2";
    border = "#fecaca";
    color = "#dc2626";
    labelColor = "#991b1b";
  } else if (!useNeutral && variant === "warning") {
    bg = "#fefce8";
    border = "#fde68a";
    color = "#d97706";
    labelColor = "#92400e";
  }

  return (
    <div
      className="text-center rounded-3"
      style={{
        backgroundColor: bg,
        border: `1px solid ${border}`,
        padding: "10px 8px",
        flex: "1 1 0",
        minWidth: 0,
      }}
    >
      <div
        className="text-uppercase fw-semibold"
        style={{
          fontSize: "0.8rem",
          letterSpacing: "0.5px",
          marginBottom: "4px",
          color: labelColor,
        }}
      >
        {label}
      </div>
      <div className="fw-bold" style={{ fontSize: "1.25rem", color }}>
        {value}
      </div>
    </div>
  );
}

function RevisionCard({
  title,
  revision,
  isLocked,
  badge,
  buttonLabel,
  buttonDisabled,
  tooltipText,
  hideButton,
  noPermission,
  onButtonClick,
}: {
  title: string;
  revision: Revision;
  isLocked: boolean;
  badge?: { label: string; variant: "current" | "latest" };
  buttonLabel: string;
  buttonDisabled?: boolean;
  tooltipText?: string;
  hideButton?: boolean;
  noPermission?: boolean;
  onButtonClick: () => void;
}) {
  const LockIcon = isLocked ? LuLock : LuLockOpen;

  const ssidPercent = formatPercent(
    revision.ssidErrorCount,
    revision.uniqueCSPPIDCount
  );
  const midPercent = formatPercent(
    revision.midErrorCount,
    revision.uniqueCSPPIDCount
  );

  const badgeStyles =
    badge?.variant === "current"
      ? {
          backgroundColor: "rgb(5, 150, 105)",
          color: "#ffffff",
          border: "none",
        }
      : {
          backgroundColor: "#d97706",
          color: "#ffffff",
          border: "none",
        };

  const createDate = new Date(revision.revisionCreateDate);
  const isValidDate = !isNaN(createDate.getTime());

  const isCurrent = badge?.variant === "current";
  const isUnlockButton = buttonLabel === "Unlock Revision";

  const grayButton = {
    color: "#94a3b8",
    border: "1.5px solid #cbd5e1",
    backgroundColor: "#f1f5f9",
    hoverBg: "#f1f5f9",
    hoverBorder: "#cbd5e1",
    iconColor: "#94a3b8",
  };

  const tealButton = {
    color: "#ffffff",
    border: "1.5px solid rgb(5, 150, 105)",
    backgroundColor: "rgb(5, 150, 105)",
    hoverBg: "rgb(4, 120, 87)",
    hoverBorder: "rgb(4, 120, 87)",
    iconColor: "#ffffff",
  };
  const amberButton = buttonDisabled
    ? {
        color: "#92400e",
        border: "1.5px solid #fbbf24",
        backgroundColor: "#fffbeb",
        hoverBg: "#fffbeb",
        hoverBorder: "#fbbf24",
        iconColor: "#92400e",
      }
    : {
        color: "#ffffff",
        border: "1.5px solid #D97706",
        backgroundColor: "#D97706",
        hoverBg: "#b45309",
        hoverBorder: "#b45309",
        iconColor: "#ffffff",
      };

  const btnStyle = noPermission
    ? grayButton
    : isCurrent
      ? tealButton
      : amberButton;

  return (
    <div
      className="rounded-3 overflow-hidden h-100 d-flex flex-column"
      style={{
        backgroundColor: "#fff",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
      }}
    >
      <div
        className="d-flex align-items-center justify-content-between px-4 py-3"
        style={{
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: isCurrent
            ? "rgb(236, 253, 245)"
            : "rgba(253, 230, 138, 0.35)",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <LockIcon
            size={18}
            style={{
              color: isCurrent ? "rgb(4, 120, 87)" : "#d97706",
            }}
          />
          <span
            className="fw-semibold"
            style={{
              fontSize: "0.95rem",
              color: isCurrent ? "rgb(6, 78, 59)" : undefined,
            }}
          >
            {title}
          </span>
          {badge && (
            <span
              className="rounded-pill px-3 py-1"
              style={{
                fontSize: isCurrent ? "10px" : "0.75rem",
                fontWeight: 600,
                ...badgeStyles,
              }}
            >
              {badge.label}
            </span>
          )}
        </div>
        <div
          className="d-flex align-items-center gap-3 fw-semibold"
          style={{
            fontSize: "0.9rem",
            color: isCurrent ? "rgb(6, 95, 70)" : "#1a1a2e",
          }}
        >
          {isValidDate ? (
            <>
              <span className="d-flex align-items-center gap-1">
                <LuCalendar size={14} />
                {createDate.toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "2-digit",
                })}
              </span>
              <span className="d-flex align-items-center gap-1">
                <LuClock size={14} />
                {createDate.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                })}
              </span>
            </>
          ) : (
            <span>—</span>
          )}
        </div>
      </div>

      <div className="px-4 pt-5 pb-4 flex-grow-1">
        <div className="d-flex gap-2">
          <StatBox
            label="Errors"
            value={revision.totalErrors}
            variant="error"
          />
          <StatBox
            label="Warnings"
            value={revision.totalWarnings}
            variant="warning"
          />
          <StatBox label="SSID" value={ssidPercent} />
          <StatBox label="MIDs" value={midPercent} />
        </div>
      </div>

      {!hideButton && (
        <div className="d-flex justify-content-center px-4 pt-3 pb-5">
          <span
            className="lock-btn-wrapper"
            style={{
              cursor: buttonDisabled ? "not-allowed" : "auto",
              display: "inline-flex",
            }}
          >
            {buttonDisabled && tooltipText && (
              <span role="tooltip" className="lock-btn-tooltip">
                {tooltipText}
              </span>
            )}
            <button
              type="button"
              className={`btn d-flex align-items-center justify-content-center gap-2${buttonDisabled ? " pe-none" : ""}`}
              disabled={buttonDisabled}
              onClick={onButtonClick}
              onMouseEnter={(e) => {
                if (!buttonDisabled) {
                  e.currentTarget.style.backgroundColor = btnStyle.hoverBg;
                  e.currentTarget.style.borderColor = btnStyle.hoverBorder;
                }
              }}
              onMouseLeave={(e) => {
                if (!buttonDisabled) {
                  e.currentTarget.style.backgroundColor =
                    btnStyle.backgroundColor;
                  e.currentTarget.style.border = btnStyle.border;
                }
              }}
              style={{
                backgroundColor: btnStyle.backgroundColor,
                border: btnStyle.border,
                color: btnStyle.color,
                fontSize: "0.9rem",
                fontWeight: 600,
                padding: "0.6rem 2.5rem",
                borderRadius: "0.5rem",
                pointerEvents: buttonDisabled ? "none" : "auto",
              }}
            >
              {isUnlockButton ? (
                <LuLockOpen size={16} style={{ color: btnStyle.iconColor }} />
              ) : (
                <LuLock size={16} style={{ color: btnStyle.iconColor }} />
              )}
              {buttonLabel}
            </button>
          </span>
        </div>
      )}
    </div>
  );
}

function LockedEmptyState() {
  return (
    <div
      className="rounded-3 overflow-hidden h-100 d-flex flex-column"
      style={{
        backgroundColor: "#fff",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
      }}
    >
      <div
        className="d-flex align-items-center gap-2 px-4 py-3"
        style={{
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#F3F7FA",
        }}
      >
        <LuLock size={18} className="text-muted" style={{ opacity: 0.5 }} />
        <span className="fw-semibold" style={{ fontSize: "0.95rem" }}>
          Locked Revision
        </span>
      </div>

      <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center px-4 py-5 text-center">
        <LuLock
          size={28}
          className="text-muted mb-2"
          style={{ opacity: 0.3 }}
        />
        <div className="text-muted" style={{ fontSize: "0.9rem" }}>
          No revision locked
        </div>
      </div>
    </div>
  );
}

function MostRecentEmptyState() {
  return (
    <div
      className="rounded-3 overflow-hidden h-100 d-flex flex-column"
      style={{
        backgroundColor: "#fff",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
      }}
    >
      <div
        className="d-flex align-items-center gap-2 px-4 py-3"
        style={{
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "rgba(226, 232, 240, 0.6)",
        }}
      >
        <LuLockOpen size={18} className="text-muted" style={{ opacity: 0.5 }} />
        <span className="fw-semibold" style={{ fontSize: "0.95rem" }}>
          Most Recent Revision
        </span>
      </div>

      <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center px-4 py-5 text-center">
        <div
          className="d-flex align-items-center justify-content-center rounded-circle mb-3"
          style={{
            width: 48,
            height: 48,
            backgroundColor: "#f0fdf4",
            border: "1px solid #bbf7d0",
          }}
        >
          <LuCircleCheck size={22} style={{ color: "#16a34a" }} />
        </div>
        <div className="fw-semibold mb-1" style={{ fontSize: "0.95rem" }}>
          The locked revision is the most recent
        </div>
        <div className="text-muted" style={{ fontSize: "0.85rem" }}>
          No newer revision available
        </div>
      </div>
    </div>
  );
}

export default function LockSelection({
  mostRecentRevision,
  lockedRevision,
  lockedRevisionId,
  lockDisabled,
  lockDisabledReason,
  unlockDisabled,
  unlockDisabledReason,
  hasErrors,
  canCertify,
  isLocking = false,
  isUnlocking = false,
  lockError = null,
  unlockError = null,
  onLock,
  onUnlock,
  onReplaceLock,
}: LockSelectionProps) {
  const {
    dialog,
    showMostRecent,
    hasLockedRevision,
    isCurrentlyLocked,
    lockedIsMostRecent,
    lockTooltipText,
    unlockTooltipText,
    handleLockClick,
    handleUnlockClick,
    handleConfirmLock,
    handleConfirmUnlock,
    handleConfirmReplace,
    handleCancelDialog,
  } = useLockSelection({
    mostRecentRevision,
    lockedRevision,
    lockedRevisionId,
    lockDisabled,
    lockDisabledReason,
    unlockDisabled,
    unlockDisabledReason,
    hasErrors,
    isLocking,
    isUnlocking,
    lockError,
    unlockError,
    onLock,
    onUnlock,
    onReplaceLock,
  });

  return (
    <div>
      <div className="row g-4 mb-4 mt-1">
        <div className="col-6">
          {showMostRecent && !lockedIsMostRecent ? (
            <RevisionCard
              title="Most Recent Revision"
              revision={mostRecentRevision!}
              isLocked={false}
              badge={{ label: "Latest", variant: "latest" }}
              buttonLabel="Lock This Revision"
              buttonDisabled={!canCertify || lockDisabled}
              noPermission={!canCertify}
              tooltipText={
                !canCertify
                  ? "You don't have permission to lock revisions"
                  : lockTooltipText
              }
              onButtonClick={handleLockClick}
            />
          ) : (
            <MostRecentEmptyState />
          )}
        </div>

        <div className="col-6">
          {hasLockedRevision && lockedRevision ? (
            <RevisionCard
              title="Locked Revision"
              revision={lockedRevision}
              isLocked={isCurrentlyLocked}
              badge={{ label: "Current", variant: "current" }}
              buttonLabel="Unlock Revision"
              buttonDisabled={
                !canCertify || unlockDisabled || !isCurrentlyLocked
              }
              noPermission={!canCertify}
              tooltipText={
                !canCertify
                  ? "You don't have permission to unlock revisions"
                  : unlockTooltipText
              }
              hideButton={!isCurrentlyLocked}
              onButtonClick={handleUnlockClick}
            />
          ) : (
            <LockedEmptyState />
          )}
        </div>
      </div>

      <ConfirmationDialog
        open={dialog === "lock"}
        title="Lock This Revision?"
        message="Locking this revision will set it as the current version for certification. You can unlock it later if needed. Are you sure you want to proceed?"
        confirmLabel="Yes, Lock Revision"
        confirmVariant="amber"
        confirmIcon={<LuLock size={15} />}
        isLoading={isLocking}
        onConfirm={handleConfirmLock}
        onCancel={handleCancelDialog}
      />

      <ConfirmationDialog
        open={dialog === "unlock"}
        title="Unlock Revision?"
        message="Unlocking this revision will force the certification to be done against the most recent version. This action cannot be undone. Are you sure you want to continue?"
        confirmLabel="Yes, Unlock"
        confirmVariant="primary"
        confirmIcon={<LuLockOpen size={15} />}
        isLoading={isUnlocking}
        onConfirm={handleConfirmUnlock}
        onCancel={handleCancelDialog}
      />

      <ConfirmationDialog
        open={dialog === "replace"}
        title="Replace Locked Revision?"
        message="You already have a locked revision for this certification cycle. If you update to lock this version, you will not be able to use the other revision. Are you sure you want to continue?"
        confirmLabel="Yes, Replace & Lock"
        confirmVariant="amber"
        confirmIcon={<LuLock size={15} />}
        isLoading={isLocking}
        onConfirm={handleConfirmReplace}
        onCancel={handleCancelDialog}
      />

      <style>{`
        .lock-btn-wrapper {
          position: relative;
        }
        .lock-btn-tooltip {
          display: none;
          position: absolute;
          bottom: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          background: #1e293b;
          color: #fff;
          font-size: 0.8rem;
          font-weight: 500;
          padding: 6px 12px;
          border-radius: 6px;
          white-space: nowrap;
          z-index: 10;
          pointer-events: none;
        }
        .lock-btn-tooltip::after {
          content: "";
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border-width: 5px;
          border-style: solid;
          border-color: #1e293b transparent transparent transparent;
        }
        .lock-btn-wrapper:hover .lock-btn-tooltip {
          display: block;
        }
      `}</style>
    </div>
  );
}
